import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { apiRequest, ApiError } from "../api/client";
import type { AdminSession } from "../api/types";

interface AuthContextValue {
  session: AdminSession | null;
  /** True only while the very first session check is still in flight —
   * distinct from `session === null` (checked, and confirmed logged out),
   * so the UI can show a loading state instead of flashing the login page
   * first. */
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AdminSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // On first load, the only way to know if a valid session cookie already
  // exists is to try using it — there is nothing in localStorage to check
  // anymore. A successful refresh call doubles as "am I logged in".
  useEffect(() => {
    apiRequest<AdminSession>("/admin/refresh-session", { method: "POST", skipRefresh: true })
      .then(setSession)
      .catch(() => setSession(null))
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await apiRequest<AdminSession>("/admin/login", {
      method: "POST",
      body: { email, password },
      skipRefresh: true,
    });
    setSession(result);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiRequest("/admin/logout", { method: "POST", skipRefresh: true });
    } catch {
      // Even if the network call fails, still clear local state — the
      // user's intent to log out shouldn't be blocked by a flaky request.
    }
    setSession(null);
  }, []);

  return <AuthContext.Provider value={{ session, isLoading, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { ApiError };
